import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { sendEmail } from '../services/emailService';
import { 
  Mail,
  Phone,
  MessageSquare,
  ArrowRight
} from 'lucide-react';

const Guests = () => {
  const [formData, setFormData] = useState({
    email: '',
    phone: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Show loading message
    toast.loading('Sending your inquiry...');
    
    try {
      // Create full form data for email
      const emailFormData = {
        name: 'Guest User',
        email: formData.email,
        phone: formData.phone,
        message: 'Guest inquiry for property information and availability.',
        inquiryType: 'Property Inquiry'
      };
      
      const result = await sendEmail(emailFormData, 'Guest Inquiry', 'Guests Page');
      
      if (result.success) {
        toast.success('Thank you for your inquiry! We will contact you within 24 hours.');
        setFormData({
          email: '',
          phone: ''
        });
      } else {
        toast.error('Failed to send inquiry. Please try again or contact us directly.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to send inquiry. Please try again or contact us directly.');
    }
  };

  // Premium property images for visual appeal
  const propertyImages = [
    {
      url: "https://images.unsplash.com/photo-1561501900-3701fa6a0864?w=800",
      location: "Dubai, UAE"
    },
    {
      url: "https://images.unsplash.com/photo-1757839939579-d71ac270993b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxsYWdvcyUyMGx1eHVyeSUyMGhvdGVsfGVufDB8fHx8MTc1OTg2MzgwOXww&ixlib=rb-4.1.0&q=85",
      location: "Lagos, Africa"
    },
    {
      url: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800",
      location: "New York, USA"
    },
    {
      url: "https://images.unsplash.com/photo-1737807478491-6e258b44bd04?w=800",
      location: "Miami, USA"
    },
    {
      url: "https://images.unsplash.com/photo-1743592889729-d8a92388412e?w=800",
      location: "Los Angeles, USA"
    },
    {
      url: "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800",
      location: "London, UK"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1551918120-9739cb430c6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZXNvcnR8ZW58MHx8fHwxNzU5NTE5MjgyfDA&ixlib=rb-4.1.0&q=85)'
          }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-6xl font-extralight text-white mb-8 leading-tight">
            Premium Properties
            <span className="block font-light text-white/90">Worldwide</span>
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto font-light leading-relaxed">
            Discover our curated collection of luxury accommodations across premier global destinations.
          </p>
        </div>
      </section>

      {/* Property Images Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-6">Premium Properties</h2>
            <p className="text-xl text-gray-600 font-light">Luxury accommodations in the world's most sought-after destinations</p>
          </div>

          {/* Images Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {propertyImages.map((image, index) => (
              <div key={index} className="group relative overflow-hidden rounded-xl">
                <img 
                  src={image.url} 
                  alt={`Luxury property in ${image.location}`}
                  className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <p className="text-white text-lg font-light">{image.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-6">
              Get In Touch
            </h2>
            <p className="text-xl text-gray-600 font-light">
              Contact us for information about our exclusive properties and services
            </p>
          </div>

          <Card className="shadow-xl border-0">
            <CardHeader className="bg-emerald-600 text-white text-center rounded-t-lg">
              <CardTitle className="text-2xl font-light">
                <MessageSquare className="inline-block mr-3 h-6 w-6" />
                Contact Form
              </CardTitle>
            </CardHeader>
            <CardContent className="p-12">
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="your.email@example.com"
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-3">
                      Phone Number *
                    </label>
                    <Input
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+1 (555) 123-4567"
                      className="h-12"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-light py-6"
                >
                  Send Message
                  <ArrowRight className="ml-3 h-5 w-5" />
                </Button>
              </form>

              <div className="mt-8 text-center">
                <p className="text-sm text-gray-600 font-light">
                  Our team will respond to your inquiry within 24 hours.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Guests;